/**
 * 기술지원팀 월간보고서 — 보기·결재 모달 (v2.113.0 · 2026-09-02)
 *
 * ⭐**읽기 전용이다.** 이 문서의 알맹이는 기술지원 포털이 갖고 있고, 우리는 접수해
 *   결재를 태울 뿐이다. 포털에서 내용을 고치면 양쪽 판이 갈리므로 수정 칸을 두지 않는다
 *   (서버도 같은 이유로 수정·첨부 교체를 거절한다 — 화면만 숨기면 API 는 뚫린 셈이 된다).
 *   정정은 기술지원 포털에서 **새 회차**로 다시 올린다.
 *
 * 결재(승인·반려·회수)는 지출결재와 **같은 API**를 쓴다 — 여기서 다시 만들지 않는다.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/expense-api';
import { periodText } from '../lib/tsp-report-api';
import { EXPENSE_STATUS_BADGE } from './ExpenseDocModal';
import { signatureImageUrl } from '../lib/approval-api';
import { DocHistoryList } from './DocHistoryList';
import { openServerPdfPreview } from '../lib/pdf-preview';
import { LoadingLines } from './ui/Skeleton';

interface Props {
  docId: number;
  onClose: () => void;
}

export function TspReportModal({ docId, onClose }: Props) {
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['expense', 'doc', docId],
    queryFn: () => api.getExpenseDoc(docId),
  });

  const doc = detail.data?.doc ?? null;
  const t = detail.data?.tspReport ?? null;
  const receipt = detail.data?.receipts?.[0] ?? null;

  const act = useMutation({
    mutationFn: (kind: 'approve' | 'reject' | 'recall') => {
      if (kind === 'approve') return api.approveExpenseDoc(docId);
      if (kind === 'recall') {
        // ⭐완결 문서 회수는 사유 필수 (v2.101.0) — 다른 결재 모달과 같은 규칙
        if (doc?.status === '최종승인') {
          const r = window.prompt('완결(최종승인) 문서를 회수합니다.\n사유를 적어 주세요 (필수 — 처리 이력에 남습니다)') ?? '';
          if (!r.trim()) return Promise.reject(new Error('사유가 없으면 완결 문서를 회수할 수 없습니다'));
          return api.recallExpenseDoc(docId, r.trim());
        }
        return api.recallExpenseDoc(docId);
      }
      const reason = window.prompt('반려 사유를 적어 주세요 (선택)\n\n※ 기술지원 포털에서 고쳐 새 회차로 다시 올리게 됩니다.') ?? '';
      return api.rejectExpenseDoc(docId, reason);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  const del = useMutation({
    mutationFn: () => api.deleteExpenseDoc(docId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[70] flex items-start justify-center overflow-y-auto p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded shadow-xl w-full max-w-3xl my-6">
        {/* 머리 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <h2 className="ui-modal-title shrink-0">기술지원팀 월간보고서</h2>
            {doc && <span className="text-xs text-slate-500 font-mono shrink-0">{doc.docNo}</span>}
            {doc && (
              <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${EXPENSE_STATUS_BADGE[doc.status] ?? ''}`}>
                {doc.status}
              </span>
            )}
            {t?.isTest && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0">
                연결 시험
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} className="ui-close">✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {detail.isLoading && <div className="text-sm text-slate-400 py-6 text-center"><LoadingLines /></div>}

          {t?.isTest && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2">
              기술지원 포털이 <b>연결 시험</b>으로 올린 문서입니다. 결재선을 타지 않으며,
              확인 후 아래 [🗑 삭제]로 지우시면 됩니다.
            </div>
          )}

          {doc?.rejectReason && (
            <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2">
              반려 사유: {doc.rejectReason}
            </div>
          )}

          {/* 보고 개요 — 사람이 쓴 문서가 아니라서 「어디서 온 무엇인가」를 먼저 보여 준다 */}
          {t && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">보고 개요</div>
              <table className="w-full text-[13px] border border-slate-200 rounded overflow-hidden">
                <tbody>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2 w-[130px]">대상 기간</th>
                    <td className="px-3 py-2">
                      <b>{periodText(t.periodYm)}</b>
                      <span className="text-slate-500 text-xs ml-1.5">({t.periodYm})</span>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">제목</th>
                    <td className="px-3 py-2">{doc?.purpose ?? ''}</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">요약</th>
                    <td className="px-3 py-2">
                      {t.summary || <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">작성자</th>
                    <td className="px-3 py-2">
                      {doc?.authorName}
                      <span className="text-slate-500 text-xs ml-1.5">
                        {doc?.authorDept ?? ''} {doc?.authorTitle ?? ''}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">접수</th>
                    <td className="px-3 py-2 text-slate-600 text-[13px]">
                      기술지원 포털 <span className="font-mono">{t.extDocNo}</span> · {t.revision}회차
                      {t.receivedAt && <span className="text-slate-400 ml-1.5">· {t.receivedAt.slice(0, 16)}</span>}
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="text-[12px] text-slate-500 mt-1.5">
                보고서 본문은 기술지원 포털에서 만든 PDF 입니다 — 아래 [📄 보고서 보기]로 확인하세요.
                내용을 고치려면 <b>기술지원 포털에서 고쳐 다시 올립니다</b>(포털에서는 수정할 수 없습니다).
              </p>
            </section>
          )}

          {/* 결재판 — 지출결재·휴일근무일지와 같은 모양 */}
          {detail.data && detail.data.cells.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">결재</div>
              <div className="flex gap-2">
                {detail.data.cells.map((c) => (
                  <div key={c.role} className="border border-slate-300 rounded text-center w-[86px]">
                    <div className="text-[11px] bg-slate-100 py-0.5 text-slate-600">{c.role}</div>
                    <div className="h-9 flex items-center justify-center text-xs">
                      {c.skipped ? (
                        <span className="text-slate-300">생략</span>
                      ) : c.stamped ? (
                        c.userId && c.hasSignature ? (
                          <img src={signatureImageUrl(c.userId)} alt={c.name}
                            className="max-h-[30px] max-w-[70px] object-contain"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          <span className="font-semibold text-slate-700">{c.name}</span>
                        )
                      ) : (
                        <span className="text-slate-300">대기</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 pb-0.5">
                      {c.skipped ? '' : (c.stampedAt ?? '').slice(2, 10)}
                      {!c.skipped && c.delegated && (
                        <span className="ml-0.5 text-[11px] text-amber-600 font-semibold">(대결)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <DocHistoryList history={detail.data?.history} />
        </div>

        {/* 단추 */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
          <div className="flex gap-2">
            {receipt && (
              <button type="button"
                onClick={() => openServerPdfPreview({
                  pdfUrl: api.attachmentDownloadUrl(receipt.id),
                  filename: receipt.filename,
                })}
                className="btn btn-secondary">
                📄 보고서 보기
              </button>
            )}
            {detail.data?.attachmentId && (
              <button type="button"
                onClick={() => openServerPdfPreview({
                  pdfUrl: api.attachmentDownloadUrl(detail.data!.attachmentId!),
                  filename: `${doc?.docNo ?? '기술지원팀월간보고서'}.pdf`,
                })}
                className="btn btn-secondary">
                🗂 결재본 보기
              </button>
            )}
            {detail.data?.canDelete && (
              <button type="button"
                onClick={() => {
                  if (window.confirm(`${doc?.docNo ?? '이 문서'} 를 삭제할까요?\n기술지원 포털에는 「없는 건」으로 보이게 됩니다.`)) del.mutate();
                }}
                disabled={del.isPending}
                className="btn btn-danger">
                {del.isPending ? '삭제 중…' : '🗑 삭제'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {detail.data?.canRecall && (
              <button type="button" onClick={() => act.mutate('recall')} disabled={act.isPending}
                className="btn btn-secondary">↩ 승인 회수</button>
            )}
            {detail.data?.canReject && (
              <button type="button" onClick={() => act.mutate('reject')} disabled={act.isPending}
                className="btn btn-danger">반려</button>
            )}
            {detail.data?.canApprove && (
              <button type="button" onClick={() => act.mutate('approve')} disabled={act.isPending}
                className="btn btn-success-solid">승인</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
